package org.example.project.ui

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.material3.Button
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.unit.dp
import org.example.project.model.CellState
import org.example.project.model.ShipType
import org.example.project.viewmodel.GameViewModel
import androidx.compose.foundation.Image
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.ui.layout.ContentScale
import org.jetbrains.compose.resources.painterResource
import battleships.shared.generated.resources.*
import battleships.shared.generated.resources.Res
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Surface
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import org.example.project.model.PlacedShip
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.sp
import androidx.compose.runtime.getValue
import androidx.compose.runtime.setValue
import kotlinx.coroutines.delay


@Composable
fun ShipPlacementScreen(
    viewModel: GameViewModel,
    opponentName: String = "Opponent",
    opponentReady: Boolean = false
) {

    BoxWithConstraints(
        modifier = Modifier.fillMaxSize()
    ) {

        val isMobile = maxWidth < 700.dp
        var timerTick by remember { mutableStateOf(0) }

        LaunchedEffect(Unit) {
            while (true) {
                delay(1000)
                timerTick++
            }
        }


        val remaining = run {
            timerTick
            viewModel.multiplayerGame?.placementSecondsRemaining() ?: 90
        }



        val cellSize =
            if (isMobile)
                ((maxWidth - 80.dp) / 10)
                    .coerceIn(24.dp, 40.dp)
            else
                40.dp

        val titleSize =
            if (isMobile)
                28.sp
            else
                42.sp

        val shipImageHeight =
            if (isMobile)
                30.dp
            else
                40.dp

        val shipImageWidth =
            if (isMobile)
                90.dp
            else
                120.dp
        Image(
            painter = painterResource(Res.drawable.ocean),
            contentDescription = null,
            modifier = Modifier.fillMaxSize(),
            contentScale = ContentScale.Crop
        )

        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(if (isMobile) 12.dp else 24.dp)
                .verticalScroll(rememberScrollState()),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            Text(
                text = "PLACE YOUR FLEET",
                style = MaterialTheme.typography.headlineLarge.copy(fontSize = titleSize),
                color = Color(0xFF00D9FF)
            )

            Spacer(Modifier.height(8.dp))

            Text(
                text = "Opponent: $opponentName — ${if (opponentReady) "Ready" else "Placing ships..."}",
                color = if (opponentReady) Color(0xFF00FF00) else Color(0xFFFFAA00)
            )

            Spacer(Modifier.height(16.dp))

            Surface(
                color = Color.Black.copy(alpha = 0.45f),
                border = BorderStroke(1.dp, Color(0xFF00CFFF)),
                shape = RoundedCornerShape(12.dp)
            ) {
                Column(
                    modifier = Modifier.padding(horizontal = 18.dp, vertical = 10.dp),
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {

                    if (!viewModel.fleetDeployed) {

                        Text(
                            text = "DEPLOYMENT TIMER",
                            color = Color(0xFF00CFFF)
                        )

                        Text(
                            text = "${remaining}s",
                            fontSize = 30.sp,
                            color = when {
                                remaining <= 10 -> Color.Red
                                remaining <= 30 -> Color(0xFFFF9800)
                                else -> Color(0xFF00CFFF)
                            }
                        )

                    } else {

                        Text(
                            text = "FLEET DEPLOYED",
                            color = Color(0xFF00FF88),
                            fontSize = 22.sp
                        )

                        Spacer(Modifier.height(6.dp))

                        Text(
                            text = "Waiting for opponent...",
                            color = Color.LightGray
                        )
                    }
                }
            }

            Spacer(Modifier.height(20.dp))

            Text(
                text = "Selected Ship: ${
                    viewModel.selectedShip?.name ?: "ALL PLACED"
                }"
            )
            viewModel.selectedShip?.let {

                Text(
                    "Length: ${it.size}",
                    color = Color.Cyan
                )
            }

            Spacer(Modifier.height(12.dp))

            OutlinedButton(
                onClick = {
                    viewModel.changeOrientation()
                },
                border = BorderStroke(
                    2.dp,
                    Color.Cyan
                ),
                colors = ButtonDefaults.outlinedButtonColors(
                    containerColor = Color(0xAA001F3F),
                    contentColor = Color.Cyan
                )
            ) {
                Text(
                    text = "ROTATE SHIP\nCurrent: ${viewModel.orientation}",
                    textAlign = TextAlign.Center
                )
            }

            Spacer(Modifier.height(20.dp))

            LazyRow(
                horizontalArrangement = Arrangement.spacedBy(if (isMobile) 8.dp else 12.dp)
            ) {

                items(ShipType.entries) { ship ->

                    val selected =
                        viewModel.selectedShip == ship

                    val placed =
                        viewModel.placedShips.contains(ship)

                    Card(
                        modifier = Modifier
                            .clickable {
                                if (!placed)
                                    viewModel.selectShip(ship)
                            },
                        colors = CardDefaults.cardColors(
                            containerColor =
                                if (selected)
                                    Color(0xFF004A6E)
                                else
                                    Color(0xAA001F3F)
                        ),
                        border = BorderStroke(
                            2.dp,
                            if (placed)
                                Color.Green
                            else if (selected)
                                Color.Cyan
                            else
                                Color.Gray
                        )
                    ) {

                        Column(
                            modifier = Modifier.padding(12.dp),
                            horizontalAlignment =
                                Alignment.CenterHorizontally
                        ) {

                            ShipImage(
                                ship = ship,
                                width = shipImageWidth,
                                height = shipImageHeight
                            )

                            Spacer(
                                Modifier.height(8.dp)
                            )

                            Text(
                                ship.name,
                                color = Color.Cyan
                            )
                        }
                    }
                }
            }

            Spacer(Modifier.height(30.dp))

            Row {
                Spacer(Modifier.width(30.dp))

                for (i in 1..10) {
                    Text(
                        "$i",
                        color = Color.Cyan,
                        modifier = Modifier.width(cellSize)
                    )
                }
            }

            PlacementGrid(
                viewModel = viewModel,
                cellSize = cellSize
            )

            Spacer(Modifier.height(15.dp))

            FleetStatus(
                viewModel = viewModel,
                isMobile = isMobile
            )

            Spacer(Modifier.height(30.dp))

            Button(
                enabled = viewModel.shipsPlaced && !viewModel.fleetDeployed,
                colors = ButtonDefaults.buttonColors(
                    containerColor = Color.Cyan
                ),
                onClick = {
                    viewModel.deployFleet()
                }
            ) {

                Text(
                    if (viewModel.fleetDeployed) "FLEET DEPLOYED" else "DEPLOY FLEET",
                    color = Color.Black,
                    textAlign = TextAlign.Center
                )
            }
        }
    }
}



@Composable
private fun PlacementGrid(
    viewModel: GameViewModel,
    cellSize: Dp
) {

    Column {

        for (row in 0 until 10) {

            Row {

                for (col in 0 until 10) {

                    val state =
                        viewModel.playerBoard
                            .cells[row][col]

                    val ship =
                        findShipAt(
                            row,
                            col,
                            viewModel.deployedShips
                        )

                    val isShipStart =
                        ship?.row == row &&
                                ship?.col == col


                    val color =
                        when (state) {

                            CellState.SHIP ->
                                Color(0xFF2196F3)

                            else ->
                                Color(0xFF0A192F)
                        }

                    Box(
                        modifier = Modifier
                            .size(cellSize)
                            .padding(1.dp)
                            .background(color)
                            .border(
                                1.dp,
                                Color(0xFF012B42)
                            )
                            .clickable {

                                val existingShip =
                                    viewModel.shipAt(
                                        row,
                                        col
                                    )

                                if (existingShip != null) {

                                    viewModel.removeShip(
                                        existingShip
                                    )

                                } else {

                                    viewModel.placeShip(
                                        row,
                                        col
                                    )
                                }
                            },
                                contentAlignment =
                                Alignment.Center
                    ){
                        if (isShipStart) {

                            val image =
                                when(ship.type) {

                                    ShipType.CARRIER ->
                                        Res.drawable.big_ship1

                                    ShipType.BATTLESHIP ->
                                        Res.drawable.big_ship2

                                    ShipType.CRUISER ->
                                        Res.drawable.med_ship1

                                    ShipType.SUBMARINE ->
                                        Res.drawable.med_ship2

                                    ShipType.DESTROYER ->
                                        Res.drawable.small_ship1
                                }

                            Image(
                                painter = painterResource(image),
                                contentDescription = null,
                                modifier =
                                    Modifier
                                        .width(if(ship.horizontal) cellSize * ship.type.size else cellSize)
                                        .height(if(ship.horizontal) cellSize else cellSize * ship.type.size)
                                        .graphicsLayer {
                                            if(!ship.horizontal)
                                                rotationZ = 90f
                                        }
                            )
                        }
                    }
                }
            }
        }
    }
}

@Composable
fun ShipImage(
    ship: ShipType,
    width: Dp,
    height: Dp
) {

    val drawable =
        when(ship) {

            ShipType.CARRIER ->
                Res.drawable.big_ship1

            ShipType.BATTLESHIP ->
                Res.drawable.big_ship2

            ShipType.CRUISER ->
                Res.drawable.med_ship1

            ShipType.SUBMARINE ->
                Res.drawable.med_ship2

            ShipType.DESTROYER ->
                Res.drawable.small_ship1
        }

    Image(
        painter =
            painterResource(drawable),
        contentDescription = null,
        modifier =
            Modifier
                .height(height)
                .width(width),
        contentScale =
            ContentScale.Fit
    )
}

@Composable
fun FleetStatus(
    viewModel: GameViewModel,
    isMobile: Boolean
) {

    Card(
        modifier = Modifier.fillMaxWidth(if (isMobile) 0.9f else 0.4f),
        colors = CardDefaults.cardColors(
            containerColor = Color(0xAA001F3F)
        ),
        border = BorderStroke(
            2.dp,
            Color.Cyan
        )
    ) {

        Column(
            modifier = Modifier.padding(
                if (isMobile)
                    10.dp
                else
                    16.dp
            ),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {

            Text(
                text = "FLEET STATUS",
                color = Color.Cyan,
                textAlign = TextAlign.Center,
                modifier = Modifier.fillMaxWidth()
            )
            Spacer(Modifier.height(8.dp))

            ShipType.entries.forEach { ship ->

                val placed =
                    viewModel.placedShips.contains(ship)

                Text(
                    text = "${ship.name} ${if (placed) "(PLACED)" else "(NOT PLACED)"}",
                    color =
                        if (placed)
                            Color.Green
                        else
                            Color.Red,
                    textAlign = TextAlign.Center,
                    modifier = Modifier.fillMaxWidth()
                )
            }
        }
    }
}


fun findShipAt(
    row: Int,
    col: Int,
    ships: List<PlacedShip>
): PlacedShip? {

    return ships.find { ship ->

        repeat(ship.type.size) { i ->

            val r =
                if(ship.horizontal)
                    ship.row
                else
                    ship.row + i

            val c =
                if(ship.horizontal)
                    ship.col + i
                else
                    ship.col

            if (
                r == row &&
                c == col
            ) {
                return@find true
            }
        }

        false
    }
}